import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type ModelState } from '../api/client';
import { PageHeader } from '../components/Layout';
import { Badge, Pagination, QueryBoundary, Table, Td, Th, toneForModelState } from '../components/ui';
import { dateTime } from '../lib/format';

const LIMIT = 50;
const STATES: ModelState[] = [
  'training',
  'validated',
  'paper_test',
  'approved',
  'archived',
  'rejected',
];

export default function Models() {
  const [state, setState] = useState<ModelState | ''>('');
  const [offset, setOffset] = useState(0);

  const q = useQuery({
    queryKey: ['models', state, offset],
    queryFn: () => api.models({ state: state || undefined, limit: LIMIT, offset }),
  });

  return (
    <>
      <PageHeader
        title="Models"
        description="The ML registry. Models are trained by scripts.train (see Operations) and land here VALIDATED or REJECTED; promotion is an audited operator action."
      />

      <div className="mb-4 flex flex-wrap gap-1.5">
        <button
          onClick={() => {
            setState('');
            setOffset(0);
          }}
          className={`rounded border px-2 py-1 text-xs ${state === '' ? 'border-slate-500 text-slate-100' : 'border-edge text-slate-400'}`}
        >
          all
        </button>
        {STATES.map((s) => (
          <button
            key={s}
            onClick={() => {
              setState(s);
              setOffset(0);
            }}
            className={`rounded border px-2 py-1 text-xs ${state === s ? 'border-slate-500 text-slate-100' : 'border-edge text-slate-400'}`}
          >
            {s}
          </button>
        ))}
      </div>

      <QueryBoundary q={q} isEmpty={q.data?.items.length === 0}>
        {q.data && (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Model</Th>
                  <Th>Algorithm</Th>
                  <Th>State</Th>
                  <Th>Feature set</Th>
                  <Th>Created</Th>
                  <Th>Changed by</Th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((m) => (
                  <tr key={m.model_id} className="hover:bg-slate-800/30">
                    <Td>
                      <Link className="text-sky-400 hover:underline" to={`/models/${m.model_id}`}>
                        {m.model_id}
                      </Link>
                    </Td>
                    <Td>{m.algorithm}</Td>
                    <Td>
                      <Badge tone={toneForModelState(m.state)}>{m.state}</Badge>
                    </Td>
                    <Td className="text-xs text-slate-400">{m.feature_set_version}</Td>
                    <Td className="text-xs text-slate-500">{dateTime(m.created_at_utc)}</Td>
                    <Td className="text-xs text-slate-500">{m.state_changed_by ?? '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination total={q.data.total} limit={LIMIT} offset={offset} onChange={setOffset} />
          </>
        )}
      </QueryBoundary>
    </>
  );
}
